import {useEffect, useRef} from "react";

import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {AuthModalVariant} from "../../auth/components/auth_modal/AuthModalHeaderVariants";
import {closeFlow, startRegistrationFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {getPhoneDataFromString} from "../../form/utils/get_phone_data_from_string";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    onSilentLeadSuccess?: (sentApplication: IApplicationData | null) => Promise<void>;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const useSilentLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, onSilentLeadSuccess, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const draftSourceFormStepRef = useRef<ApplicationModalStep>(ApplicationModalStep.PROMOTION_FORM);

    const isPromotionFlow = mainLead.flowVariant === "promotion";
    const isSilentLeadFlow = mainLead.flowVariant === "silent_lead";

    const getDraftPhone = (draft: IPromotionLeadDraft) => `${draft.phone.code} ${draft.phone.number}`;
    const normalizePhone = (phone: string | null | undefined) => (phone || "").replace(/\s/g, "");
    const isDraftPhoneVerifiedForProfile = (draft: IPromotionLeadDraft, profile: IUserProfile | null | undefined) =>
        Boolean(profile?.phone_verified && normalizePhone(profile.phone) === normalizePhone(getDraftPhone(draft)));

    const getDraftWithUserProfileContactData = (draft: IPromotionLeadDraft, profile: IUserProfile): IPromotionLeadDraft => ({
        ...draft,
        name: profile.name || draft.name,
        email: profile.email || draft.email,
        phone: profile.phone ? getPhoneDataFromString(profile.phone) : draft.phone
    });

    const getResolvedDraftAfterAuthentication = (draft: IPromotionLeadDraft, profile: IUserProfile): IPromotionLeadDraft => {
        if (isSilentLeadFlow || authActionDuringPromotionFlowRef.current === AuthActionType.LOGIN) {
            return getDraftWithUserProfileContactData(draft, profile);
        }

        return draft;
    };

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = getDraftPhone(draft);
        const shouldPatchProfile = profile?.name !== draft.name || profile?.phone !== draftPhone;

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const sendDraftLead = async (draft: IPromotionLeadDraft) => {
        const {vendorId: _vendorId, offerId: _offerId, propertyId: _propertyId, source: _source, sourceSection: _sourceSection, ...leadFormValues} = draft;

        return await sendLead(leadFormValues, () => handleModalClose(), isPromotionFlow ? {persistFormValues: false} : undefined);
    };

    const completeSilentLead = async (application: IApplicationData | null) => {
        if (onSilentLeadSuccess) {
            await onSilentLeadSuccess(application);
            return;
        }

        handleModalClose();
    };

    const finalizeSilentLead = async (draft: IPromotionLeadDraft) => {
        const application = await sendDraftLead(draft);

        await completeSilentLead(application || null);
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});

        try {
            const application = await sendDraftLead(draft);

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const finalizeLeadAfterAuthentication = async (draft: IPromotionLeadDraft) => {
        if (isPromotionFlow) {
            await finalizePromotionLead(draft);
            return;
        }

        await finalizeSilentLead(draft);
    };

    const continueLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            const resolvedDraft = getResolvedDraftAfterAuthentication(draft, profile);
            dispatch(setPromotionLeadDraft(resolvedDraft));

            if (isDraftPhoneVerifiedForProfile(resolvedDraft, profile)) {
                await finalizeLeadAfterAuthentication(resolvedDraft);
                return;
            }

            await syncDraftToUserProfile(resolvedDraft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const handleDraftSubmit = async (values: IApplicationFormValues) => {
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        draftSourceFormStepRef.current = modalState.step;

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!
        };

        dispatch(setPromotionLeadDraft(draft));

        if (isAuthenticated && userProfile) {
            const resolvedDraft = getResolvedDraftAfterAuthentication(draft, userProfile);
            dispatch(setPromotionLeadDraft(resolvedDraft));

            try {
                if (isDraftPhoneVerifiedForProfile(resolvedDraft, userProfile)) {
                    await finalizeLeadAfterAuthentication(resolvedDraft);
                    return;
                }

                await syncDraftToUserProfile(resolvedDraft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        if (isAuthenticated) {
            try {
                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        const authUserSource = UserSource.REGISTRATION;
        const authModalVariant = isPromotionFlow ? "hidden_prices_variant_b" : "default";
        const authTitleVariant = isPromotionFlow ? AuthModalVariant.PROMOTION_LEAD : AuthModalVariant.APPLICATION_LEAD;

        try {
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: `${draft.phone.code} ${draft.phone.number}`,
                type: UserAccountType.LOCAL,
                source: authUserSource,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: authUserSource,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
        } catch (_error) {
            dispatch(setContinuePromotionFlowAfterAuth(true));
            sendModalEvent({type: "PROMOTION_AUTH_REQUIRED"});
            dispatch(
                startRegistrationFlow({
                    userSource: authUserSource,
                    modalVariant: authModalVariant,
                    titleVariant: authTitleVariant,
                    showLoginForm: false,
                    propertyId: isPromotionFlow ? undefined : (draft.propertyId ?? undefined),
                    offerId: isPromotionFlow ? undefined : (draft.offerId ?? undefined)
                })
            );
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizeLeadAfterAuthentication(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent({type: "PROMOTION_EDIT_PHONE", step: draftSourceFormStepRef.current});
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continueLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber
    };
};
import {useEffect, useRef} from "react";

import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {AuthModalVariant} from "../../auth/components/auth_modal/AuthModalHeaderVariants";
import {closeFlow, startRegistrationFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {getPhoneDataFromString} from "../../form/utils/get_phone_data_from_string";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    onSilentLeadSuccess?: (sentApplication: IApplicationData | null) => Promise<void>;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const useSilentLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, onSilentLeadSuccess, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const draftSourceFormStepRef = useRef<ApplicationModalStep>(ApplicationModalStep.PROMOTION_FORM);

    const isPromotionFlow = mainLead.flowVariant === "promotion";
    const isSilentLeadFlow = mainLead.flowVariant === "silent_lead";

    const getDraftPhone = (draft: IPromotionLeadDraft) => `${draft.phone.code} ${draft.phone.number}`;
    const normalizePhone = (phone: string | null | undefined) => (phone || "").replace(/\s/g, "");
    const isDraftPhoneVerifiedForProfile = (draft: IPromotionLeadDraft, profile: IUserProfile | null | undefined) =>
        Boolean(profile?.phone_verified && normalizePhone(profile.phone) === normalizePhone(getDraftPhone(draft)));

    const getDraftWithUserProfileContactData = (draft: IPromotionLeadDraft, profile: IUserProfile): IPromotionLeadDraft => ({
        ...draft,
        name: profile.name || draft.name,
        email: profile.email || draft.email,
        phone: profile.phone ? getPhoneDataFromString(profile.phone) : draft.phone
    });

    const getResolvedDraftAfterAuthentication = (draft: IPromotionLeadDraft, profile: IUserProfile): IPromotionLeadDraft => {
        if (isSilentLeadFlow || authActionDuringPromotionFlowRef.current === AuthActionType.LOGIN) {
            return getDraftWithUserProfileContactData(draft, profile);
        }

        return draft;
    };

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = getDraftPhone(draft);
        const shouldPatchProfile = profile?.name !== draft.name || profile?.phone !== draftPhone;

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const sendDraftLead = async (draft: IPromotionLeadDraft) => {
        const {vendorId: _vendorId, offerId: _offerId, propertyId: _propertyId, source: _source, sourceSection: _sourceSection, ...leadFormValues} = draft;

        return await sendLead(leadFormValues, () => handleModalClose(), isPromotionFlow ? {persistFormValues: false} : undefined);
    };

    const completeSilentLead = async (application: IApplicationData | null) => {
        if (onSilentLeadSuccess) {
            await onSilentLeadSuccess(application);
            return;
        }

        handleModalClose();
    };

    const finalizeSilentLead = async (draft: IPromotionLeadDraft) => {
        const application = await sendDraftLead(draft);

        await completeSilentLead(application || null);
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});

        try {
            const application = await sendDraftLead(draft);

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const finalizeLeadAfterAuthentication = async (draft: IPromotionLeadDraft) => {
        if (isPromotionFlow) {
            await finalizePromotionLead(draft);
            return;
        }

        await finalizeSilentLead(draft);
    };

    const continueLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            const resolvedDraft = getResolvedDraftAfterAuthentication(draft, profile);
            dispatch(setPromotionLeadDraft(resolvedDraft));

            if (isDraftPhoneVerifiedForProfile(resolvedDraft, profile)) {
                await finalizeLeadAfterAuthentication(resolvedDraft);
                return;
            }

            await syncDraftToUserProfile(resolvedDraft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const handleDraftSubmit = async (values: IApplicationFormValues) => {
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        draftSourceFormStepRef.current = modalState.step;

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!
        };

        dispatch(setPromotionLeadDraft(draft));

        if (isAuthenticated && userProfile) {
            const resolvedDraft = getResolvedDraftAfterAuthentication(draft, userProfile);
            dispatch(setPromotionLeadDraft(resolvedDraft));

            try {
                if (isDraftPhoneVerifiedForProfile(resolvedDraft, userProfile)) {
                    await finalizeLeadAfterAuthentication(resolvedDraft);
                    return;
                }

                await syncDraftToUserProfile(resolvedDraft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        if (isAuthenticated) {
            try {
                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        const authUserSource = UserSource.REGISTRATION;
        const authModalVariant = isPromotionFlow ? "hidden_prices_variant_b" : "default";
        const authTitleVariant = isPromotionFlow ? AuthModalVariant.PROMOTION_LEAD : AuthModalVariant.APPLICATION_LEAD;

        try {
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: `${draft.phone.code} ${draft.phone.number}`,
                type: UserAccountType.LOCAL,
                source: authUserSource,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: authUserSource,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
        } catch (_error) {
            dispatch(setContinuePromotionFlowAfterAuth(true));
            sendModalEvent({type: "PROMOTION_AUTH_REQUIRED"});
            dispatch(
                startRegistrationFlow({
                    userSource: authUserSource,
                    modalVariant: authModalVariant,
                    titleVariant: authTitleVariant,
                    showLoginForm: false,
                    propertyId: isPromotionFlow ? undefined : (draft.propertyId ?? undefined),
                    offerId: isPromotionFlow ? undefined : (draft.offerId ?? undefined)
                })
            );
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizeLeadAfterAuthentication(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent({type: "PROMOTION_EDIT_PHONE", step: draftSourceFormStepRef.current});
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continueLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber
    };
};
